import { useState } from "react";
import { Box, Chip, Button, Tab, Tooltip } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import DnsIcon from "@mui/icons-material/Dns";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SidePanel from "../../../../components/SidePanel";
import { tagColor as staticTagColor, assetIdentity } from "./assetUtils";
import AssetDetailContent from "./AssetDetailContent";

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const online = status === "online";
  return (
    <Chip
      label={status.toUpperCase()}
      size="small"
      sx={{
        bgcolor: online ? "#4cceac22" : "#ff444422",
        color:   online ? "#4cceac"   : "#ff6644",
        fontSize: 10,
        height: 20,
        fontWeight: 700,
        letterSpacing: 0.6,
      }}
    />
  );
};

const AssetSidePanel = ({
  row,
  open,
  onClose,
  onShowIpHistory,
  tagColor = staticTagColor,
  zone,
  ackMap,
  onAcknowledge,
  onReopen,
  onOpenFullPage,
}) => {
  // Same two-tab split as the full Asset Detail page — the drawer is a narrower
  // window onto the same content, not a different information architecture.
  const [tab, setTab] = useState("identity");

  if (!row) {
    return <SidePanel open={open} onClose={onClose} title="Asset details" />;
  }

  // Identity falls back hostname → IP → MAC. `asset_key` is an internal primary
  // key and is deliberately never shown.
  const title = assetIdentity(row) || "Asset";
  const subtitle = row.ip && title !== row.ip ? row.ip : row.mac || "";

  const contentProps = {
    row,
    tagColor,
    onShowIpHistory,
    zone,
    assetId: row.id,
    ackMap,
    onAcknowledge,
    onReopen,
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      icon={<DnsIcon sx={{ fontSize: 22 }} />}
      title={title}
      subtitle={subtitle}
      width={640}
      actions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StatusBadge status={row.status} />
          {onOpenFullPage && (
            <Tooltip title="Open full details">
              <Button
                size="small"
                endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
                onClick={() => onOpenFullPage(row.id)}
                sx={{ fontSize: 11, textTransform: "none" }}
              >
                Full details
              </Button>
            </Tooltip>
          )}
        </Box>
      }
    >
      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}>
          <TabList
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 36, "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 12.5, minHeight: 36, py: 0 } }}
          >
            <Tab label="Identity" value="identity" />
            <Tab label="Security" value="security" />
          </TabList>
        </Box>

        {/* The 640px drawer stays single-column in both tabs — two columns at this
            width would leave neither readable. */}
        <TabPanel value="identity" sx={{ p: 0 }}>
          <AssetDetailContent section="identity" {...contentProps} />
        </TabPanel>
        <TabPanel value="security" sx={{ p: 0 }}>
          <AssetDetailContent section="security" {...contentProps} />
        </TabPanel>
      </TabContext>
    </SidePanel>
  );
};

export default AssetSidePanel;
